/**
 * useReSettlement Hook
 * 재마감 관련 로직 관리
 *
 * @description
 * - 재마감 테이블 목록 조회
 * - 협력사/라이더 목록 조회 (연계 선택)
 * - 재마감 적용 처리
 * - 컴포넌트 마운트 시 자동 데이터 로드
 */
import { useState, useEffect, useCallback } from 'react';
import { reSettlementApi } from '../api/reSettlementApi';
import {
    ReSettlementTableOption,
    ReSettlementRequest,
    ReSettlementSupplier,
    ReSettlementRider,
} from '../types/reSettlement.types';
import { useToast } from '@/shared/components/feedback/Toast/ToastContext';
import { ToastType } from '@/shared/components/feedback/Toast/Toast.types';
import { removeDateHyphens } from '@/shared/utils/dateUtils';

/**
 * 재마감 관리 훅
 * @returns {Object} 재마감 관련 상태 및 함수
 * @property {boolean} loading - 재마감 적용 로딩 상태
 * @property {ReSettlementTableOption[]} tableOptions - 테이블 옵션 목록
 * @property {boolean} tableOptionsLoading - 테이블 목록 로딩 상태
 * @property {ReSettlementSupplier[]} suppliers - 협력사 목록
 * @property {boolean} suppliersLoading - 협력사 목록 로딩 상태
 * @property {ReSettlementRider[]} riders - 라이더 목록
 * @property {boolean} ridersLoading - 라이더 목록 로딩 상태
 * @property {Function} applyReSettlement - 재마감 적용 함수
 * @property {Function} fetchTableOptions - 테이블 목록 조회 함수
 * @property {Function} fetchRiders - 라이더 목록 조회 함수
 */
export const useReSettlement = () => {
    const { showToast } = useToast();
    const [loading, setLoading] = useState(false);
    const [tableOptions, setTableOptions] = useState<ReSettlementTableOption[]>([]);
    const [tableOptionsLoading, setTableOptionsLoading] = useState(false);

    // 협력사/라이더 상태
    const [suppliers, setSuppliers] = useState<ReSettlementSupplier[]>([]);
    const [suppliersLoading, setSuppliersLoading] = useState(false);
    const [riders, setRiders] = useState<ReSettlementRider[]>([]);
    const [ridersLoading, setRidersLoading] = useState(false);

    /**
     * 테이블 목록 조회
     */
    const fetchTableOptions = useCallback(async () => {
        try {
            setTableOptionsLoading(true);
            const options = await reSettlementApi.getTables();
            const optionsArray = Array.isArray(options) ? options : [];
            setTableOptions(optionsArray);
        } catch (error) {
            showToast('재마감 테이블 목록을 불러오는데 실패했습니다.', { type: ToastType.ERROR });
            setTableOptions([]);
        } finally {
            setTableOptionsLoading(false);
        }
    }, [showToast]);

    /**
     * 협력사 목록 조회
     */
    const fetchSuppliers = useCallback(async () => {
        try {
            setSuppliersLoading(true);
            const list = await reSettlementApi.getSuppliers();
            setSuppliers(Array.isArray(list) ? list : []);
        } catch {
            setSuppliers([]);
        } finally {
            setSuppliersLoading(false);
        }
    }, []);

    /**
     * 라이더 목록 조회
     * @param suppId 협력사 ID
     */
    const fetchRiders = useCallback(async (suppId: string) => {
        if (!suppId) {
            setRiders([]);
            return;
        }
        try {
            setRidersLoading(true);
            const list = await reSettlementApi.getRiders(suppId);
            setRiders(Array.isArray(list) ? list : []);
        } catch {
            setRiders([]);
        } finally {
            setRidersLoading(false);
        }
    }, []);

    /**
     * 재마감 적용
     * @param tableName 선택된 테이블명
     * @param settlementDate 정산일자 (YYYY-MM-DD 형식)
     * @param suppId 협력사 ID (선택)
     * @param riderId 라이더 ID (선택)
     */
    const applyReSettlement = useCallback(async (
        tableName: string,
        settlementDate: string,
        suppId?: string,
        riderId?: string
    ) => {
        try {
            setLoading(true);
            const formattedDate = removeDateHyphens(settlementDate);
            const request: ReSettlementRequest = {
                targetDate: formattedDate,
                tableName: tableName || undefined,
                suppId: suppId || undefined,
                riderId: riderId || undefined,
            };
            const response = await reSettlementApi.applyReSettlement(request);
            
            // 응답이 없거나 success 필드가 없는 경우도 성공으로 처리
            // (백엔드가 비동기로 처리하고 즉시 응답을 반환하는 경우)
            if (!response || response.success === undefined || response.success === true) {
                showToast('재마감이 적용되었습니다.', { type: ToastType.SUCCESS });
                return response;
            } else {
                showToast(response.message || '재마감 적용에 실패했습니다.', { type: ToastType.ERROR });
                return null;
            }
        } catch (error) {
            showToast('재마감 적용에 실패했습니다.', { type: ToastType.ERROR });
            return null;
        } finally {
            setLoading(false);
        }
    }, [showToast]);

    // 컴포넌트 마운트 시 테이블 목록 및 협력사 목록 조회
    useEffect(() => {
        fetchTableOptions();
        fetchSuppliers();
    }, [fetchTableOptions, fetchSuppliers]);

    return {
        loading,
        tableOptions,
        tableOptionsLoading,
        applyReSettlement,
        fetchTableOptions,
        // 협력사/라이더
        suppliers,
        suppliersLoading,
        riders,
        ridersLoading,
        fetchRiders,
    };
};

